import { logger } from '@heidi/runtime/logger';
import { TrackingKey } from '@heidi/runtime/performance';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useStore } from 'jotai';
import {
  GENERATE_EXAMPLE_NOTE_STREAMING_KEY,
  GET_TEMPLATE_CREATION_RECORD_BY_ID,
} from '../../contracts';
import { exampleGenerationStreamStateAtomFamily } from './atoms';
import { trackStreamRun } from './streamPerfTracking';
import {
  appendToVersionControlField,
  createStreamStateHandlers,
  updateStreamStates,
  updateVersionControlInQueryData,
} from './streamStateHelpers';
import {
  type GenerateExampleFromStructureRequest,
  streamExampleFromStructureGeneration,
} from './streaming';
import { type AnimationType, StreamStatus } from './types';

export { GENERATE_EXAMPLE_NOTE_STREAMING_KEY };

interface GenerateExampleFromStructureMutationRequest extends GenerateExampleFromStructureRequest {
  animationType: AnimationType;
}

export const useMutateGenerateExampleFromStructure = () => {
  const queryClient = useQueryClient();
  const store = useStore();

  return useMutation({
    mutationKey: [GENERATE_EXAMPLE_NOTE_STREAMING_KEY],
    onMutate: ({
      template_creation_record_id: templateCreationRecordId,
      version_control_id: versionControlId,
      animationType,
    }) => {
      const streamStateAtom = exampleGenerationStreamStateAtomFamily({
        templateCreationRecordId,
        versionControlId,
      });
      updateStreamStates({
        store,
        atoms: [streamStateAtom],
        status: StreamStatus.WAITING_START,
        animationType,
      });
      return { streamStateAtom };
    },
    mutationFn: async ({
      template_creation_record_id: templateCreationRecordId,
      version_control_id: versionControlId,
      animationType,
    }: GenerateExampleFromStructureMutationRequest) => {
      const streamStateAtom = exampleGenerationStreamStateAtomFamily({
        templateCreationRecordId,
        versionControlId,
      });

      const streamState = store.get(streamStateAtom);
      const queryKey = [GET_TEMPLATE_CREATION_RECORD_BY_ID, { templateCreationRecordId }];

      return trackStreamRun(
        TrackingKey.TEMPLATE_EXAMPLE_GEN,
        { animationType },
        async (markFirstToken) => {
          let accumulatedExample = '';
          let isFirstChunk = true;
          await streamExampleFromStructureGeneration(
            {
              data: {
                template_creation_record_id: templateCreationRecordId,
                version_control_id: versionControlId,
              },
              signal: streamState.abortController.signal,
            },
            createStreamStateHandlers({
              store,
              streamStateAtom,
              onMessageCallback: (chunk) => {
                if (isFirstChunk) {
                  markFirstToken();
                  updateStreamStates({
                    store,
                    atoms: [streamStateAtom],
                    status: StreamStatus.STREAMING,
                  });
                  updateVersionControlInQueryData({
                    queryClient,
                    queryKey,
                    versionControlId,
                    field: 'example_note',
                    updater: () => '',
                  });
                  isFirstChunk = false;
                }
                accumulatedExample += chunk.data;
                appendToVersionControlField({
                  queryClient,
                  queryKey,
                  versionControlId,
                  field: 'example_note',
                  chunk,
                });
              },
            })
          );
          return accumulatedExample;
        }
      );
    },
    onSuccess: (_, __, context) => {
      const { streamStateAtom } = context ?? {};
      if (streamStateAtom) {
        updateStreamStates({
          store,
          atoms: [streamStateAtom],
          status: StreamStatus.IDLE,
        });
      }
    },
    onError: (error: Error, _, context) => {
      const { streamStateAtom } = context ?? {};
      if (streamStateAtom) {
        updateStreamStates({
          store,
          atoms: [streamStateAtom],
          status: StreamStatus.ERROR,
        });
      }
      logger.error(error);
    },
  });
};
